import { Tooltip, type VirtualTableColumn } from "@snapchat/snap-design-system";
import { CopyToClipboardButton, LensIcon } from "../../../common/components";
import { Header } from "../../../common/components/table/Header";
import { TwoLineCell } from "../../../common/components/table/TwoLineCell";
import { DecreaseText } from "../../../common/components/text";
import { formatMediumDateTime } from "../../../common/formatters";
import { lensIdIsCopiedToClipboard } from "../../../common/model";
import { LensStatusCell } from "../../../groups/group-details/components/group-lenses-table/LensStatusCell";
import { LensListItemModel } from "../../models";

// Unfortunately SDS doesn't export this interface
interface CellRenderer {
    row: {
        index: number;
    };
    data: LensListItemModel[];
}

function getRenderer(fn: (group: LensListItemModel) => JSX.Element) {
    return ({ row, data }: CellRenderer) => fn(data[row.index] as LensListItemModel);
}

export function getColumns(): VirtualTableColumn<LensListItemModel>[] {
    return [
        {
            id: "image",
            width: 72,
            Cell: getRenderer((lens) => <LensIcon src={lens.iconUrl} source={lens.lensSourceInfo.name as string} />),
        },
        {
            id: "name",
            accessor: "name",
            width: 315,
            Header: <Header>Name / ID</Header>,
            Cell: getRenderer((lens) => (
                <TwoLineCell text={lens.name} subText={lens.compoundId.id as string}>
                    <CopyToClipboardButton
                        copyValue={lens.compoundId.id as string}
                        notification={lensIdIsCopiedToClipboard}
                        tooltip="Copy Lens ID"
                    />
                </TwoLineCell>
            )),
        },
        {
            id: "webSdkVersion",
            width: 102,
            Header: (
                <Header>
                    <Tooltip
                        content="Minimum Camera Kit web SDK version required to support this Lens."
                        textAlign="center"
                    >
                        <span>Min Web SDK Version</span>
                    </Tooltip>
                </Header>
            ),
            Cell: getRenderer((lens) => (
                <DecreaseText>{lens.minWebSdkVersion ? lens.minWebSdkVersion : "Unknown"}</DecreaseText>
            )),
        },
        {
            id: "sdkVersion",
            width: 102,
            Header: (
                <Header>
                    <Tooltip
                        content="Minimum Camera Kit mobile SDK version required to support this Lens."
                        textAlign="center"
                    >
                        <span>Min Mobile SDK Version</span>
                    </Tooltip>
                </Header>
            ),
            Cell: getRenderer((lens) => (
                <DecreaseText>{lens.minSdkVersion ? lens.minSdkVersion : "Unknown"}</DecreaseText>
            )),
        },
        {
            id: "updatedAt",
            accessor: "updatedAt",
            width: 170,
            Header: <Header>Last Updated</Header>,
            Cell: getRenderer((lens) => <DecreaseText>{formatMediumDateTime(lens.updatedAt)}</DecreaseText>),
        },
        {
            id: "status",
            width: 97,
            Header: (
                <Header>
                    <Tooltip
                        content="This field refers to the visibility of your Lens on the Snapchat App.
                        To manage Lens visibility, please navigate to My Lenses."
                        textAlign="center"
                    >
                        <span>Status</span>
                    </Tooltip>
                </Header>
            ),
            Cell: getRenderer((lens) => <LensStatusCell status={lens.lensStatus} />),
        },
        {
            id: "lensSource",
            Header: <Header>Lens Source</Header>,
            Cell: getRenderer((lens) => <DecreaseText>{lens.scheduleV3Alias}</DecreaseText>),
        },
    ];
}
